import type { RefObject } from 'react';
import { useCallback, useLayoutEffect, useState } from 'react';

export type NavigationOverflow = {
    /** Positions of the scope's entries set aside behind the "…" entry. */
    readonly hidden: readonly number[];
    /** Whether the footer's links are set aside too. */
    readonly linksHidden: boolean;
};

export type NavigationMeasures = {
    /** The room the entries and the footer share. */
    readonly available: number;
    readonly gap: number;
    /** The footer's links, with the gap before what follows them. */
    readonly linksHeight: number;
    readonly moreHeight: number;
    /** The entry holding keyboard focus, which stays in place: an entry's position, or `units.length` for the links. */
    readonly pinned: number | null;
    /** The footer without its links. */
    readonly tailHeight: number;
    /** Each entry's height with its margins; a group counts as one entry. */
    readonly units: readonly number[];
};

const NO_OVERFLOW: NavigationOverflow = { hidden: [], linksHidden: false };

const requiredHeight = (
    { gap, linksHeight, moreHeight, tailHeight, units }: NavigationMeasures,
    hidden: ReadonlySet<number>,
): number => {
    const visible = units.filter((_, index) => !hidden.has(index));
    const hasMore = hidden.size > 0;
    const rows = visible.length + (hasMore ? 1 : 0);

    return (
        visible.reduce((sum, height) => sum + height, 0) +
        gap * Math.max(rows - 1, 0) +
        (hasMore ? moreHeight : 0) +
        tailHeight +
        (hidden.has(units.length) ? 0 : linksHeight)
    );
};

/**
 * Which entries go behind "…" so the rail never scrolls. The footer's links go first, then the scope's entries from
 * the bottom up, a group always whole; the links return when the entries set aside left room for them. The entry holding focus is skipped, so unfolding a group from the keyboard
 * never takes the focused control away.
 */
export const fitNavigationUnits = (measures: NavigationMeasures): NavigationOverflow => {
    const hidden = new Set<number>();

    if (requiredHeight(measures, hidden) <= measures.available) {
        return NO_OVERFLOW;
    }

    const bottomUp = [measures.units.length, ...measures.units.map((_, index) => index).reverse()].filter(
        (index) => index !== measures.pinned,
    );

    for (const index of bottomUp) {
        hidden.add(index);

        if (requiredHeight(measures, hidden) <= measures.available) {
            break;
        }
    }

    // The links went first, but once a large group has made room they come back if they fit: they sit apart below
    // the entries, so showing them keeps the entries' order.
    const linksIndex = measures.units.length;
    if (hidden.has(linksIndex)) {
        hidden.delete(linksIndex);

        if (requiredHeight(measures, hidden) > measures.available) {
            hidden.add(linksIndex);
        }
    }

    return {
        hidden: [...hidden].filter((index) => index < measures.units.length).sort((left, right) => left - right),
        linksHidden: hidden.has(measures.units.length),
    };
};

const outerHeight = (element: Element): number => {
    const style = getComputedStyle(element);

    return (
        element.getBoundingClientRect().height +
        Number.parseFloat(style.marginTop) +
        Number.parseFloat(style.marginBottom)
    );
};

const focusedPosition = (elements: readonly HTMLElement[]): number | null => {
    const focused = document.activeElement;
    const position = elements.findIndex((element) => focused !== null && element.contains(focused));

    return position === -1 ? null : position;
};

const isSameOverflow = (left: NavigationOverflow, right: NavigationOverflow): boolean =>
    left.linksHidden === right.linksHidden && left.hidden.join() === right.hidden.join();

export const MORE_ENTRY_ATTRIBUTE = 'data-nav-more';

type OverflowRefs = {
    readonly container: RefObject<HTMLElement | null>;
    /** Holds the scope's list, which remounts with the scope, so it is looked up on each reading. */
    readonly entries: RefObject<HTMLElement | null>;
    readonly links: RefObject<HTMLElement | null>;
    readonly tail: RefObject<HTMLElement | null>;
};

/**
 * Measures the rail with every entry shown, then sets aside what does not fit. The entries stay mounted and are only
 * hidden, so they are unhidden for the reading and hidden again before the browser paints.
 */
export const useNavigationOverflow = (refs: OverflowRefs, moreHeight: number): NavigationOverflow => {
    const [overflow, setOverflow] = useState<NavigationOverflow>(NO_OVERFLOW);

    const measure = useCallback((): void => {
        const container = refs.container.current;
        const links = refs.links.current;
        const list = refs.entries.current?.querySelector('ul') ?? null;
        const tail = refs.tail.current;

        if (container === null || links === null || list === null || tail === null) {
            return;
        }

        const children = [...list.children].filter((child): child is HTMLElement => child instanceof HTMLElement);
        const units = children.filter((child) => !child.hasAttribute(MORE_ENTRY_ATTRIBUTE));
        const more = children.filter((child) => child.hasAttribute(MORE_ENTRY_ATTRIBUTE));
        const shown = [...units, links];
        const wasHidden = shown.map((element) => element.hidden);
        const wasMoreHidden = more.map((element) => element.hidden);

        for (const element of shown) {
            element.hidden = false;
        }

        for (const element of more) {
            element.hidden = true;
        }

        const tailGap = Number.parseFloat(getComputedStyle(tail).rowGap) || 0;
        const linksHeight = links.childElementCount === 0 ? 0 : links.getBoundingClientRect().height + tailGap;
        const measures: NavigationMeasures = {
            available: container.clientHeight,
            gap: Number.parseFloat(getComputedStyle(list).rowGap) || 0,
            linksHeight,
            moreHeight,
            pinned: focusedPosition([...units, links]),
            tailHeight: tail.getBoundingClientRect().height - linksHeight,
            units: units.map(outerHeight),
        };

        shown.forEach((element, index) => {
            element.hidden = wasHidden[index] ?? false;
        });
        more.forEach((element, index) => {
            element.hidden = wasMoreHidden[index] ?? false;
        });

        const next = fitNavigationUnits(measures);

        setOverflow((current) => (isSameOverflow(current, next) ? current : next));
    }, [moreHeight, refs.container, refs.entries, refs.links, refs.tail]);

    // Every render can change what the rail holds, a group folding or the scope switching, so each one re-measures.
    useLayoutEffect(measure);

    useLayoutEffect(() => {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(measure);

        for (const ref of [refs.container, refs.entries, refs.tail]) {
            if (ref.current !== null) {
                observer.observe(ref.current);
            }
        }

        return () => observer.disconnect();
    }, [measure, refs.container, refs.entries, refs.tail]);

    return overflow;
};
